import { Alert, AlertDescription, AlertTitle, Separator, Spinner } from '@monksflow/monks-ui';
import { AlertCircleIcon } from 'lucide-react';
import {
  useOpportunitySchedule,
  useUpdateOpportunitySchedule,
} from '@/features/admin/model/useOpportunitySchedule';
import {
  ScheduleEditor,
  type ScheduleFormData,
} from '@/features/admin/ui/ToolConfigPage/ScheduleEditor';
import { notificationService } from '@/lib/services/notification.service';
import { useTranslation } from 'react-i18next';

interface OpportunityScheduleConfigProps {
  /** Workspaces whose opportunity configuration is complete, from `useAgentConfig`. */
  fullyConfiguredWorkspaceIds: string[];
}

export const OpportunityScheduleConfig = ({
  fullyConfiguredWorkspaceIds,
}: OpportunityScheduleConfigProps) => {
  const { t } = useTranslation('admin');

  const { data: schedule, isLoading } = useOpportunitySchedule();
  const updateMutation = useUpdateOpportunitySchedule();

  // A complete workspace configuration is what creates the opportunities job the cron picks up
  // (EtlJobManager does it on config save), so it is the whole precondition: with none, this
  // schedule has nothing to drive and the API rejects the save.
  const isUnschedulable = fullyConfiguredWorkspaceIds.length === 0;

  // Notifications fire directly at the mutation call site (no-use-effect skill,
  // Rule 3: side-effects belong in event handlers, not `useEffect(() => notify)`
  // reactors watching `isSuccess`/`isError` flags).
  const handleSave = (data: ScheduleFormData) => {
    updateMutation.mutate(data, {
      onSuccess: () => {
        notificationService.showSuccess(
          t('admin.opportunityScheduleConfig.scheduleUpdatedSuccessfully'),
          { description: t('admin.opportunityScheduleConfig.changesWillTakeEffectFrom') },
        );
      },
      onError: () => {
        notificationService.showError(t('admin.opportunityScheduleConfig.failedToUpdateSchedule'), {
          description: t('admin.opportunityScheduleConfig.pleaseTryAgain'),
        });
      },
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Separator className="my-6" />

      <div className="space-y-2">
        <h2 className="text-lg font-semibold">
          {t('admin.opportunityScheduleConfig.opportunityRunSchedule')}
        </h2>
        <p className="text-sm text-muted-foreground">
          {t('admin.opportunityScheduleConfig.configureWhenAutomatedOpportunityDiscovery')}
        </p>
      </div>

      {isUnschedulable && (
        <Alert variant="default" icon={<AlertCircleIcon />}>
          <AlertTitle>{t('admin.opportunityScheduleConfig.nothingToScheduleYet')}</AlertTitle>
          <AlertDescription>
            {t('admin.opportunityScheduleConfig.noWorkspaceIsReadyTo')}
          </AlertDescription>
        </Alert>
      )}

      <ScheduleEditor
        schedule={schedule}
        onSave={handleSave}
        isSaving={updateMutation.isPending}
        disableSave={isUnschedulable}
      />
    </div>
  );
};
